/**
 * Cloud
 * @modules user
 */
import { CloudActions, DefaultState } from '@portal/portal-core';
import type { AnyAction } from 'redux';

// Boundary: cloud-core's types omit these two action-type constants (they are
// undefined at runtime as well); widen the access to preserve the existing
// switch cases without altering runtime behavior.
const CloudActionsLegacy = CloudActions as typeof CloudActions & {
  STORE_PRODUCT_ADD_USERS_RESULT?: string;
  CLEAR_PRODUCT_ADD_USERS_RESULT?: string;
};

export interface EmailVerificationState {
  userId: string;
  emailAddress: string;
  uiState: string;
  token?: string;
}

// DEPRECATE AFTER AUTH REFACTOR IS FULLY REGRESSION TESTED
const defaultEmailVerificationState = {
  userId: '',
  emailAddress: '',
  uiState: '',
};

export function emailVerification(inState: EmailVerificationState | undefined, action: AnyAction) {
  let state = inState;
  if (!state)
    state = {
      userId: '',
      emailAddress: '',
      uiState: '',
    };
  switch (action.type) {
    case CloudActions.EMAIL_VERIFICATION_FETCHING:
      return {
        ...state,
        uiState: CloudActions.EMAIL_VERIFICATION_FETCHING,
      };
    case CloudActions.EMAIL_VERIFICATION_TOKEN_SEND:
      return {
        ...state,
        userId: action.userId,
        emailAddress: action.emailAddress,
      };
    case CloudActions.EMAIL_VERIFICATION_TOKEN_RESEND:
      return {
        ...state,
        userId: action.userId,
        emailAddress: action.emailAddress,
      };
    case CloudActions.EMAIL_VERIFICATION_TOKEN_VERIFY:
      return {
        ...state,
        userId: action.userId,
        emailAddress: action.emailAddress,
        token: action.token,
      };
    case CloudActions.EMAIL_VERIFICATION_FAILED:
      return {
        ...state,
        uiState: CloudActions.EMAIL_VERIFICATION_FAILED,
      };
    case CloudActions.EMAIL_VERIFICATION_UNVERIFIED:
      return {
        ...state,
        uiState: CloudActions.EMAIL_VERIFICATION_UNVERIFIED,
        userId: action.userId,
        emailAddress: action.emailAddress,
      };
    case CloudActions.EMAIL_VERIFICATION_EXPIRED:
      return {
        ...state,
        uiState: CloudActions.EMAIL_VERIFICATION_EXPIRED,
        userId: action.userId,
        emailAddress: action.emailAddress,
      };
    case CloudActions.EMAIL_VERIFICATION_VERIFIED:
      return {
        ...state,
        uiState: CloudActions.EMAIL_VERIFICATION_VERIFIED,
      };
    case CloudActions.EMAIL_VERIFICATION_CLEAR:
      return { ...state, defaultEmailVerificationState };
    case DefaultState.DEFAULT_STATE:
      return { ...state, defaultEmailVerificationState };
    default:
      return state;
  }
}

export function userAgreements(
  state: Record<string, unknown> = { notAgreedYet: '' },
  action: AnyAction,
) {
  switch (action.type) {
    case CloudActions.USER_AGREEMENT_STOWE:
      return { ...state, ...action.data };
    case CloudActions.USER_HAS_NOT_AGREED_YET:
      return { ...state, ...action.data };
    case CloudActions.USER_HAS_AGREED:
      return { ...state, notAgreedYet: '' };
    case DefaultState.DEFAULT_STATE:
      return {};
    default:
      return state;
  }
}

export function userProductLicenses(state: Record<string, unknown> = {}, action: AnyAction) {
  switch (action.type) {
    case CloudActions.USER_PRODUCT_LICENSES:
      return { ...state, ...action.userProductLicenses };
    case DefaultState.DEFAULT_STATE:
      return {};
    default:
      return state;
  }
}

export interface UserAdminState {
  adminCostCenterList?: unknown;
  costCenter?: Record<string, unknown>;
  users?: Record<string, unknown>;
  lastUsed?: unknown;
  authorizedDevices?: Record<string, unknown>;
  productAddUsersResult?: unknown;
  suspendUsersResult?: unknown;
  productSetUsersEndDate?: unknown;
  productSetUsersArchivedDate?: unknown;
}

export function handleSetUserAdminCostCenter(state: UserAdminState, action: AnyAction) {
  const serverCostCenter = action.costCenter;
  if (serverCostCenter) {
    return { ...state, costCenter: { ...state.costCenter, ...serverCostCenter } };
  }
  return state;
}

export function handleSetUserAdminUsers(state: UserAdminState, action: AnyAction) {
  let newUsers: Record<string, unknown> = { ...state.users };

  for (const serverUserId in action.users) {
    const userJson = action.users[serverUserId];
    newUsers = { ...newUsers, [serverUserId]: userJson };
  }

  state.users = { ...state.users, ...newUsers };
  return state;
}

// @TODO this refactor was breaking, debug later for refactor
// export function handleSetUserAdminUsers(state, action) {
//   let newUsers = { ...state.users };
//   Object.keys(action.users).forEach((serverUserId) => {
//     const userJson = action.users[serverUserId];
//     newUsers = { ...newUsers, [serverUserId]: userJson };
//   });
//   return { ...state, users: { ...state.users, ...newUsers } };
// }

export function handleSetUserAdminAuthorizedDevices(state: UserAdminState, action: AnyAction) {
  let newAuthorizedDevices: Record<string, unknown> = { ...state.authorizedDevices };
  // TODO: refactor to eliminate esLint warnings. See pattern in handleSetUserAdminUsers(). refactor the whole file.

  for (const authorizedDeviceId in action.authorizedDevices) {
    const authorizedDeviceJson = action.authorizedDevices[authorizedDeviceId];
    newAuthorizedDevices = { ...newAuthorizedDevices, [authorizedDeviceId]: authorizedDeviceJson };
  }
  // TODO: refactor to return a new object rather than modifying state. See pattern in handleSetUserAdminCostCenter(). refactor the whole file

  state.authorizedDevices = { ...state.authorizedDevices, ...newAuthorizedDevices };
  return state;
}

export function handleStoreProductAddUsersResult(state: UserAdminState, action: AnyAction) {
  const { productAddUsersResult } = action;
  if (productAddUsersResult) {
    state.productAddUsersResult = {
      ...(state.productAddUsersResult as Record<string, unknown>),
      ...productAddUsersResult,
    };
  }
  return state;
}

export function handleClearProductAddUsersResult(state: UserAdminState, action: AnyAction) {
  const closedProductAddUsersResult = action.productAddUsersResult;
  if (closedProductAddUsersResult === 'closed') {
    return {
      ...state,
      productAddUsersResult: closedProductAddUsersResult,
    };
  }
  return state;
}

export function handleSuspendUsersResult(state: UserAdminState, action: AnyAction) {
  const { suspendUsersResult } = action;
  if (suspendUsersResult) {
    state.suspendUsersResult = {
      ...(state.suspendUsersResult as Record<string, unknown>),
      ...suspendUsersResult,
    };
  }
  return state;
}

export function handleClearSuspendUsersResult(state: UserAdminState, action: AnyAction) {
  const { suspendUsersResult } = action;
  if (suspendUsersResult === 'closed') {
    state = { ...state, suspendUsersResult };
  }
  return state;
}

export function handleStoreProductSetUserEndDateResult(state: UserAdminState, action: AnyAction) {
  const { productSetUsersEndDateResult } = action;
  if (productSetUsersEndDateResult) {
    state = {
      ...state,
      productSetUsersEndDate: productSetUsersEndDateResult,
    };
  }
  return state;
}

export function handleClearProductSetUserEndDateResult(state: UserAdminState, action: AnyAction) {
  const { productSetUsersEndDate } = action;
  if (productSetUsersEndDate === 'closed') {
    return {
      ...state,
      productSetUsersEndDate,
    };
  }
  return state;
}

export function handleStoreProductSetUsersArchivedDateResult(
  state: UserAdminState,
  action: AnyAction,
) {
  const { productSetUsersArchivedDateResult } = action;
  if (productSetUsersArchivedDateResult) {
    // eslint-disable-next-line no-param-reassign
    state = {
      ...state,
      productSetUsersArchivedDate: productSetUsersArchivedDateResult,
    };
  }
  return state;
}

export function handleClearProductSetUsersArchivedDateResult(
  state: UserAdminState,
  action: AnyAction,
) {
  const { productSetUsersArchivedDate } = action;
  if (productSetUsersArchivedDate === 'closed') {
    return {
      ...state,
      productSetUsersArchivedDate,
    };
  }
  return state;
}

export function costCenters(state: Record<string, unknown> = {}, action: AnyAction) {
  switch (action.type) {
    case CloudActions.COST_CENTERS:
      return { ...state, ...action.costCenters };
    case DefaultState.DEFAULT_STATE:
      return {};
    default:
      return state;
  }
}

export interface AuthorizedDevicesState {
  devices: Record<string, unknown>;
  removalsRemaining: Record<string, unknown>;
}

export function authorizedDevices(
  state: AuthorizedDevicesState = { devices: {}, removalsRemaining: {} },
  action: AnyAction,
) {
  switch (action.type) {
    case CloudActions.AUTHORIZED_DEVICES:
      return {
        ...state,
        devices: {
          ...state.devices,
          ...action.authorizedDevices,
        },
      };
    case CloudActions.AUTHORIZED_DEVICES_REMOVALS_REMAINING:
      return {
        ...state,
        removalsRemaining: {
          ...state.removalsRemaining,
          ...action.authorizedDeviceRemovalsRemaining,
        },
      };
    case DefaultState.DEFAULT_STATE:
      return {};
    default:
      return state;
  }
}

export function users(state: Record<string, unknown> = {}, action: AnyAction) {
  switch (action.type) {
    case CloudActions.USERS:
      return { ...state, ...action.users };
    case DefaultState.DEFAULT_STATE:
      return {};
    default:
      return state;
  }
}

export function products(state: unknown[] = [], action: AnyAction) {
  switch (action.type) {
    case CloudActions.PRODUCTS:
      return action.products || [];
    case DefaultState.DEFAULT_STATE:
      return [];
    default:
      return state;
  }
}

export function handleAddUser(state: Record<string, unknown> = {}, action: AnyAction) {
  const { user } = action;
  const userId = user.id;
  if (state && state[userId]) {
    throw new Error(`Cannot add user ${userId}, id already exists in store.`);
  }
  return { ...state, [user.id]: user };
}

/* Utils */

// TODO don't create one here.  Switch to add.
export function handleCreateUserAction(action: AnyAction) {
  // TODO validate user
  return action.user;
}

export function userAdmin(state: UserAdminState = {}, action: AnyAction) {
  switch (action.type) {
    case CloudActions.USER_ADMIN_STORE_COST_CENTER_LIST:
      return {
        ...state,
        adminCostCenterList: action.adminCostCenterList,
      };
    case CloudActions.USER_ADMIN_SET_SELECTED_COST_CENTER:
      return {
        ...state,
        costCenter: action.costCenterObj,
        users: {},
      };
    case CloudActions.USER_ADMIN_FORCE_UPDATE_COST_CENTER_LIST:
      return {
        ...state,
        adminCostCenterList: {},
        costCenter: {},
        users: {},
      };
    case CloudActions.USER_ADMIN_STORE_CURRENT_COST_CENTER:
      return handleSetUserAdminCostCenter(state, action);
    case CloudActions.USER_ADMIN_STORE_CURRENT_USERS:
      return handleSetUserAdminUsers(state, action);
    case CloudActions.USER_ADMIN_STORE_CURRENT_LAST_USED:
      return {
        ...state,
        lastUsed: action.lastUsed,
      };
    case CloudActions.USER_ADMIN_STORE_CURRENT_AUTHORIZED_DEVICES:
      return handleSetUserAdminAuthorizedDevices(state, action);
    case CloudActions.USER_ADMIN_FORCE_UPDATE:
      return {
        ...state,
        costCenter: {
          id: action.costCenterId,
          version: 0,
        },
        users: {},
      };
    case CloudActionsLegacy.STORE_PRODUCT_ADD_USERS_RESULT:
      return handleStoreProductAddUsersResult(state, action);
    case CloudActionsLegacy.CLEAR_PRODUCT_ADD_USERS_RESULT:
      return handleClearProductAddUsersResult(state, action);
    case CloudActions.STORE_SET_USERS_SUSPENDED_RESULT:
      return handleSuspendUsersResult(state, action);
    case CloudActions.CLEAR_SET_USERS_SUSPENDED_RESULT:
      return handleClearSuspendUsersResult(state, action);
    case CloudActions.STORE_PRODUCT_SET_USERS_END_DATE_RESULT:
      return handleStoreProductSetUserEndDateResult(state, action);
    case CloudActions.CLEAR_PRODUCT_SET_USERS_END_DATE_RESULT:
      return handleClearProductSetUserEndDateResult(state, action);
    case CloudActions.STORE_PRODUCT_SET_USERS_ARCHIVED_DATE_RESULT:
      return handleStoreProductSetUsersArchivedDateResult(state, action);
    case CloudActions.CLEAR_PRODUCT_SET_USERS_ARCHIVED_DATE_RESULT:
      return handleClearProductSetUsersArchivedDateResult(state, action);
    case DefaultState.DEFAULT_STATE:
      return {};
    default:
      return state;
  }
}
